<script setup lang="ts">
  import Button from '@leaflink/stash/Button.vue';
  import Divider from '@leaflink/stash/Divider.vue';
  import Icon from '@leaflink/stash/Icon.vue';
  import Illustration from '@leaflink/stash/Illustration.vue';
  import Logo from '@leaflink/stash/Logo.vue';
  import Modal from '@leaflink/stash/Modal.vue';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';
  import { I18nT } from 'vue-i18n';

  import { SPLIT_NAMES } from '@/constants';
  import { useCompanyStore } from '@/piniaStores/company/company.store';
  import { useUserStore } from '@/piniaStores/user/user.store';
  import i18n from '@/plugins/i18n';

  const emits = defineEmits<{
    (e: 'confirm'): void;
    (e: 'reject'): void;
  }>();

  const { activeCompany } = storeToRefs(useCompanyStore());
  const isBuyer = computed(() => !!activeCompany.value?.buyer);
  const isSeller = computed(() => !!activeCompany.value?.seller);

  const userStore = useUserStore();

  const { getIsSplitEnabled } = storeToRefs(userStore);

  const isCashbackEnabled = getIsSplitEnabled.value(SPLIT_NAMES.CASHBACK_REWARDS);
</script>
<template>
  <Modal hide-header close-button-color-class="tw-text-white">
    <template #featured-content>
      <div class="tw-bg-green-200 tw-bg-post-enrollment-modal-bg tw-flex tw-items-center tw-justify-center tw-bg-cover">
        <Illustration type="vignette" name="money-bank" :size="300" />
      </div>
    </template>
    <h1>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.title') }}</h1>
    <!-- Buyer -->
    <p v-if="isBuyer" class="tw-mb-6">
      {{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.mainContent') }}
      <a :href="i18n.t('paymentOnSellThroughEnrollment.buyerLearnMoreUrl')" target="_blank">{{
        i18n.t('common.learnMore')
      }}</a>
    </p>
    <!-- Seller -->
    <p v-else class="tw-mb-6">
      {{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.mainContent') }}
      <a :href="i18n.t('paymentOnSellThroughEnrollment.sellerLearnMoreUrl')" target="_blank">{{
        i18n.t('common.learnMore')
      }}</a>
    </p>
    <div class="tw-flex tw-flex-row lg:tw-flex-col tw-gap-2 lg:tw-gap-4">
      <div class="tw-flex tw-flex-1 tw-gap-4 tw-items-center tw-flex-col lg:tw-flex-row">
        <div class="tw-flex-1 tw-w-full lg:tw-max-w-64">
          <div class="tw-bg-green-100 tw-rounded tw-text-center tw-py-4">
            <Logo brand="post" variant="lockup" />
            <!-- Buyer -->
            <div v-if="isBuyer" class="tw-text-green-700 tw-text-xl tw-font-medium">
              {{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.noFees') }}
            </div>
            <!-- Seller -->
            <template v-else>
              <I18nT
                v-if="getIsSplitEnabled('fire_pod__direct_pay_seller_fee')"
                class="tw-text-green-700 tw-text-xl tw-font-medium"
                keypath="paymentOnSellThroughEnrollment.enrollmentModal.seller.fees"
                scope="global"
                tag="div"
              >
                <template #percentage>
                  <span class="tw-text-2xl">2.5%</span>
                </template>
              </I18nT>
              <div v-else class="tw-text-green-700 tw-text-xl tw-font-medium">
                {{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.noFees') }}
              </div>
            </template>
          </div>
        </div>
        <div class="tw-flex-1 tw-text-sm tw-text-ice-900">
          <!-- Buyer -->
          <template v-if="isBuyer">
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mb-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.post.bulletTwo') }}</div>
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mb-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.post.bulletOne') }}</div>
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.post.bulletThree') }}</div>
            </div>
          </template>
          <!-- Seller -->
          <template v-else>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mb-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.post.bulletOne') }}</div>
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mb-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.post.bulletTwo') }}</div>
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.post.bulletThree') }}</div>
            </div>
          </template>
        </div>
      </div>
      <Divider class="tw-invisible lg:tw-visible" />
      <div class="tw-flex tw-flex-1 tw-gap-4 tw-items-center tw-flex-col lg:tw-flex-row">
        <div class="tw-flex-1 tw-w-full lg:tw-max-w-64">
          <div class="tw-bg-green-100 tw-rounded tw-text-center tw-py-4">
            <Logo brand="direct-pay" variant="lockup" />
            <!-- Buyer -->
            <div v-if="isBuyer" class="tw-text-green-700 tw-text-xl tw-font-medium">
              {{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.noFees') }}
            </div>
            <!-- Seller -->
            <template v-else>
              <template v-if="getIsSplitEnabled('fire_pod__direct_pay_seller_fee')">
                <I18nT
                  class="tw-text-green-700 tw-text-xl tw-font-medium"
                  keypath="paymentOnSellThroughEnrollment.enrollmentModal.seller.fees"
                  scope="global"
                  tag="div"
                >
                  <template #percentage>
                    <span class="tw-text-2xl">1.25%</span>
                  </template>
                </I18nT>
              </template>
              <div v-else class="tw-text-green-700 tw-text-xl tw-font-medium">
                {{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.noFees') }}
              </div>
            </template>
          </div>
        </div>
        <div class="tw-flex-1 tw-text-sm tw-text-ice-900">
          <!-- Buyer -->
          <template v-if="isBuyer">
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mb-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>
                {{
                  isCashbackEnabled
                    ? i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.directPay.bulletOneWithCashback')
                    : i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.directPay.bulletOne')
                }}
              </div>
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mb-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.directPay.bulletTwo') }}</div>
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.buyer.directPay.bulletThree') }}</div>
            </div>
          </template>
          <!-- Seller -->
          <template v-else>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mb-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.directPay.bulletOne') }}</div>
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2 tw-mb-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.directPay.bulletTwo') }}</div>
            </div>
            <div class="tw-flex tw-justify-start tw-items-center tw-gap-2">
              <Icon name="circle-check" class="tw-text-green" />
              <div>
                {{
                  isCashbackEnabled
                    ? i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.directPay.bulletThreeWithCashback')
                    : i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.seller.directPay.bulletThree')
                }}
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <p v-if="isSeller && getIsSplitEnabled('fire_pod__direct_pay_seller_fee')" class="tw-mt-4 tw-mb-2">
      {{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.feesDisclaimer') }}
    </p>
    <template #actions>
      <Button secondary @click="emits('reject')">{{
        i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.reject')
      }}</Button>
      <Button @click="emits('confirm')">{{ i18n.t('paymentOnSellThroughEnrollment.enrollmentModal.confirm') }}</Button>
    </template>
  </Modal>
</template>
